import Column from '@amzn/meridian/column/column';
import React, { useContext, useState } from 'react';
import { StateContext } from '../../GlobalStateStore';
import { getRequesterRole } from './helper';
import { FormattedMessage, FormattedPlural } from 'react-intl';
import UserBadge from '../Dashboard/DashboardComponents/UserBadge';
import TabGroup from '@amzn/meridian/tab/tab-group';
import Tab from '@amzn/meridian/tab/tab';
import Row from '@amzn/meridian/row/row';
import Heading from '@amzn/meridian/heading/heading';
import { MonthSelection, TmTabSelection } from './types';
import DataState from '../../Types/DataState';
import Loader from '@amzn/meridian/loader/loader';
import Box from '@amzn/meridian/box/box';
import Alert from '@amzn/meridian/alert/alert';
import { getCalendarMonth } from '@amzn/aet-coachingstats-kit';
import moment from 'moment';
import { Stats } from '../../Types';
import { getReviewedContactReviewsCountForMonth } from './OmCoachingHelper';
import { TMCoachingStatsSummaryForOM } from '../../Types/CoachingStats';

export interface TMLevelCoachingCompletionRatesProps {
  tmCoachingStatsSummary: TMCoachingStatsSummaryForOM;
  monthSelection: MonthSelection;
  batchCoachingStatsState: string;
  tmTabSelected: TmTabSelection;
  setTmTabSelected: any;
  coachingsAndContactReviews: Stats | undefined;
  coachingsAndContactReviewsState: any;
}

const TMLevelCoachingCompletionRates: React.FC<
  TMLevelCoachingCompletionRatesProps
> = ({
  tmCoachingStatsSummary,
  monthSelection,
  batchCoachingStatsState,
  tmTabSelected,
  setTmTabSelected,
  coachingsAndContactReviews,
  coachingsAndContactReviewsState,
}): React.ReactElement => {
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, isOm, requesterUserID, requesterUsername } =
    inspireOptions.data;
  const requestorRole = getRequesterRole(isOm, isTm);
  const {
    statsInfo: {
      monthlyCoachingStatsSummary,
      aggregatedBottomQuartileCoachingStatsSummary:
        bottomQuartileCoachingStatsSummary,
      aggregatedNewHireCoachingStatsSummary: newHireCoachingStatsSummary,
    },
    tmInfo: { alias, fullName, directReportCount },
  } = tmCoachingStatsSummary;

  return (
    <>
      {batchCoachingStatsState === DataState.LOADING && (
        <Row
          type='outline'
          width='100%'
          alignmentHorizontal='center'
          height='300px'
        >
          <Loader />
        </Row>
      )}
      {batchCoachingStatsState === DataState.ERROR && (
        <Box height='150px' width='fill' spacingInset='300'>
          <Alert type='error'>
            <FormattedMessage
              id='cs_perfportal_error_refresh_page'
              defaultMessage='Something went wrong. Please try refreshing the page. 
                If the problem persists, please submit a SIM ticket for us to troubleshoot.'
            />
          </Alert>
        </Box>
      )}
      {batchCoachingStatsState === DataState.LOADED &&
        coachingsAndContactReviewsState === DataState.LOADED && (
          <>
            <Row widths={['12%', '65%']} spacingInset='300'>
              <UserBadge username={alias} thumbNail />
              <Column minWidth={'150px'}>
                <Heading level={6}>{fullName}</Heading>
                {
                  <FormattedPlural
                    one={
                      <FormattedMessage
                        id='cs_perfportal_coaching_page_tm_aggregated_data_direct_report_count_singular'
                        defaultMessage={`${directReportCount} associate`}
                      />
                    }
                    other={
                      <FormattedMessage
                        id='cs_perfportal_coaching_page_tm_aggregated_data_direct_report_count_plural'
                        defaultMessage={`${directReportCount} associates`}
                      />
                    }
                    value={directReportCount}
                  />
                }
              </Column>
            </Row>
            <Row widths={['70%', '50%']} spacingInset='300'>
              <Column>
                <TabGroup value={tmTabSelected} onChange={setTmTabSelected}>
                  <Tab
                    value='tab-coachings'
                    id='tab-coachings'
                    data-csa-c-type='tab'
                    data-csa-c-slot-id='om-coaching-page-view-coachings-tab'
                    data-csa-c-tag1='OM Coaching'
                    data-csa-c-tag2={
                      monthSelection === MonthSelection.THIS_MONTH
                        ? 'current month'
                        : 'last month'
                    }
                    data-csa-c-profile-id={requesterUserID}
                    data-csa-c-username={requesterUsername}
                    data-csa-c-requester-role={requestorRole}
                  >
                    <FormattedMessage
                      id='cs_perfportal_coaching_page_tm_aggregated_data_tab_title_coachings'
                      defaultMessage='Coachings'
                    />
                  </Tab>
                  <Tab
                    value='tab-contact-reviews'
                    id='tab-contact-reviews'
                    data-csa-c-type='tab'
                    data-csa-c-slot-id='om-coaching-page-view-contact-reviews-tab'
                    data-csa-c-tag1='OM Coaching'
                    data-csa-c-tag2={
                      monthSelection === MonthSelection.THIS_MONTH
                        ? 'current month'
                        : 'last month'
                    }
                    data-csa-c-profile-id={requesterUserID}
                    data-csa-c-username={requesterUsername}
                    data-csa-c-requester-role={requestorRole}
                  >
                    <FormattedMessage
                      id='cs_perfportal_coaching_page_tm_aggregated_data_tab_title_contact_reviews'
                      defaultMessage='Contact Reviews'
                    />
                  </Tab>
                </TabGroup>
                {/* TODO: Remove the check below after May 2025. */}
                {monthSelection === MonthSelection.LAST_MONTH &&
                getCalendarMonth(moment().toISOString()).month === 5 &&
                getCalendarMonth(moment().toISOString()).year === 2025 ? (
                  <Box height='150px' width='fill' spacingInset='300'>
                    <Alert type='neutral'>
                      <FormattedMessage
                        id='cs_perfportal_no_data_before_may'
                        defaultMessage='Coaching stats are only available starting May 2025.'
                      />
                    </Alert>
                  </Box>
                ) : (
                  <>
                    {tmTabSelected === TmTabSelection.COACHINGS && (
                      <>
                        <Row>
                          <FormattedMessage
                            id={
                              monthSelection === MonthSelection.THIS_MONTH
                                ? 'cs_perfportal_coaching_page_tm_aggregated_data_coachings_delivered_this_month'
                                : 'cs_perfportal_coaching_page_tm_aggregated_data_coachings_delivered_last_month'
                            }
                            defaultMessage={`Coachings delivered ${
                              monthSelection === MonthSelection.THIS_MONTH
                                ? 'this'
                                : 'last'
                            } month`}
                          />
                        </Row>
                        <Row widths={['60%', '20%', '20%']}>
                          <Column height={'10px'}>
                            <Heading type='b200' level={3}>
                              <b>
                                <FormattedMessage
                                  id='cs_perfportal_coaching_page_tm_aggregated_data_required'
                                  defaultMessage='Required'
                                />
                              </b>
                            </Heading>
                          </Column>
                          <Column height={'10px'} alignmentHorizontal='end'>
                            <Heading type='b200' level={3}>
                              <b>
                                <FormattedMessage
                                  id='cs_perfportal_coaching_page_tm_aggregated_data_completed'
                                  defaultMessage='Completed'
                                />
                              </b>
                            </Heading>
                          </Column>
                        </Row>
                        <Row widths={['60%', '20%', '20%']}>
                          <Column height={'10px'}>
                            <Heading type='b200' level={4}>
                              <FormattedMessage
                                id='cs_perfportal_coaching_page_tm_aggregated_data_bottom_quartile_with_no_requirement_info'
                                defaultMessage='Bottom Quartile'
                              />
                            </Heading>
                          </Column>
                          <Column height={'10px'} alignmentHorizontal='end'>
                            <Heading type='b200' level={4}>
                              {`${bottomQuartileCoachingStatsSummary.completedCoachings}/${bottomQuartileCoachingStatsSummary.totalCoachingRequired}`}
                            </Heading>
                          </Column>
                        </Row>
                        <Row widths={['60%', '20%', '20%']}>
                          <Column height={'10px'}>
                            <Heading type='b200' level={4}>
                              <FormattedMessage
                                id='cs_perfportal_coaching_page_tm_aggregated_data_new_hire_with_no_requirement_info'
                                defaultMessage='New Hire'
                              />
                            </Heading>
                          </Column>
                          <Column height={'10px'} alignmentHorizontal='end'>
                            <Heading type='b200' level={4}>
                              {`${newHireCoachingStatsSummary.completedCoachings}/${newHireCoachingStatsSummary.totalCoachingRequired}`}
                            </Heading>
                          </Column>
                        </Row>
                        <Row widths={['60%', '20%', '20%']}>
                          <Column height={'10px'}>
                            <Heading type='b200' level={4}>
                              <FormattedMessage
                                id='cs_perfportal_coaching_page_tm_aggregated_data_all_other_associates_with_no_requirement_info'
                                defaultMessage='All other associates'
                              />
                            </Heading>
                          </Column>
                          <Column height={'10px'} alignmentHorizontal='end'>
                            <Heading type='b200' level={4}>
                              {`${monthlyCoachingStatsSummary.completedCoachings}/${monthlyCoachingStatsSummary.totalCoachingRequired}`}
                            </Heading>
                          </Column>
                        </Row>
                      </>
                    )}
                    {tmTabSelected === TmTabSelection.CONTACT_REVIEWS && (
                      <>
                        <Row>
                          <FormattedMessage
                            id={
                              monthSelection === MonthSelection.THIS_MONTH
                                ? 'cs_perfportal_coaching_page_tm_aggregated_data_contact_reviews_delivered_this_month'
                                : 'cs_perfportal_coaching_page_tm_aggregated_data_contact_reviews_delivered_last_month'
                            }
                            defaultMessage={`Contact reviews delivered ${
                              monthSelection === MonthSelection.THIS_MONTH
                                ? 'this'
                                : 'last'
                            } month`}
                          />
                        </Row>
                        <Row>
                          <Column
                            alignmentVertical='top'
                            alignmentHorizontal='end'
                          >
                            <Heading type='h500' level={1}>
                              {(
                                getReviewedContactReviewsCountForMonth(
                                  coachingsAndContactReviews,
                                  monthSelection
                                ) / directReportCount
                              ).toFixed(1)}
                            </Heading>
                          </Column>
                          <Column
                            alignmentVertical='bottom'
                            alignmentHorizontal='start'
                          >
                            <FormattedMessage
                              id='cs_perfportal_coaching_page_tm_aggregated_data_average_contact_reviews_per_associate'
                              defaultMessage='average contact reviews per associate per month'
                            />
                          </Column>
                        </Row>
                      </>
                    )}
                  </>
                )}
              </Column>
            </Row>
          </>
        )}
    </>
  );
};

export default TMLevelCoachingCompletionRates;
